import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import handleRegisterAsyncData, {
  handleRegisterCountAsyncData,
} from 'global-components/register/OhsModuleRegisterUtils';
import { OhsSafetyPlanViewPresets } from 'dashboard/topFilter/OhsSafetyPlanFilterModels';

import { SafetyPlanListsRecordState } from '../OhsSafetyPlanModels';
import {
  fetchSafetyPlanAllocatedListsAsync,
  fetchSafetyPlanCopyListsAsync,
  fetchSafetyPlanHistoryListAsync,
  fetchSafetyPlanHistoryListCountAsync,
  fetchSafetyPlanListsAsync,
  fetchSafetyPlanListsCountAsync,
  fetchSafetyPlanScheduledListsAsync,
  fetchSafetyPlanScheduledListsCountAsync,
} from './OhsSafetyPlanActions';

const defaultSafetyPlanLists = {
  items: [],
  pagination: {
    page: 0,
    totalPages: 0,
    next: '',
  },
};
const initialState: SafetyPlanListsRecordState = {
  isLoading: false,
  safetyPlanLists: null,
  safetyPlanScheduledLists: null,
  safetyPlanHistoryList: null,
  allocatedList: null,
  safetyPlanCopyList: {
    isLoading: false,
    copyList: [],
  },
  currentPage: 1,
  currentViewPreset: OhsSafetyPlanViewPresets.View2SafetyPlanList,
};

export const safetyPlanSlice = createSlice({
  name: 'safetyPlan',
  initialState,
  reducers: {
    setCurrentRegisterPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSafetyPlanIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setViewPreset: (state, action: PayloadAction<OhsSafetyPlanViewPresets>) => {
      state.currentViewPreset = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // LIST
      .addCase(fetchSafetyPlanListsAsync.pending, (state) => {
        state.isLoading = true;
        state.safetyPlanLists = defaultSafetyPlanLists;
      })
      .addCase(fetchSafetyPlanListsAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const safetyPlanList = handleRegisterAsyncData(
            action.payload,
            'safetyPlanListtotalPages'
          );
          state.isLoading = false;
          state.safetyPlanLists = safetyPlanList;
          state.currentPage = safetyPlanList.pagination.page ?? 1;
        }
      })
      .addCase(fetchSafetyPlanListsAsync.rejected, (state) => {
        state.safetyPlanLists = defaultSafetyPlanLists;
      })
      .addCase(fetchSafetyPlanAllocatedListsAsync.pending, (state) => {
        state.allocatedList = [];
      })
      .addCase(fetchSafetyPlanAllocatedListsAsync.fulfilled, (state, action) => {
        state.allocatedList = action.payload ?? [];
        state.isLoading = false;
      })
      .addCase(fetchSafetyPlanCopyListsAsync.pending, (state) => {
        state.safetyPlanCopyList.copyList = [];
        state.safetyPlanCopyList.isLoading = true;
      })
      .addCase(fetchSafetyPlanCopyListsAsync.fulfilled, (state, action) => {
        state.safetyPlanCopyList.copyList = action.payload.items ?? [];
        state.safetyPlanCopyList.isLoading = false;
      })
      .addCase(fetchSafetyPlanListsCountAsync.fulfilled, (state, action) => {
        const safetyPlanListCountResult: any = action.payload;
        if (state.safetyPlanLists && safetyPlanListCountResult) {
          const countSafetyPlanLists = handleRegisterCountAsyncData(
            safetyPlanListCountResult,
            state.safetyPlanLists,
            'safetyPlanListtotalPages'
          );
          state.safetyPlanLists = countSafetyPlanLists;
        }
      })

      // SCHEDULE
      .addCase(fetchSafetyPlanScheduledListsAsync.pending, (state) => {
        state.isLoading = true;
        state.safetyPlanLists = defaultSafetyPlanLists;
        state.safetyPlanScheduledLists = defaultSafetyPlanLists;
      })
      .addCase(fetchSafetyPlanScheduledListsAsync.fulfilled, (state, action) => {
        const scheduledListsCountResult: any = action.payload;
        if (action.payload && scheduledListsCountResult) {
          const safetyPlanScheduled = handleRegisterAsyncData(
            action.payload,
            'safetyPlanScheduledListtotalPages'
          );
          state.isLoading = false;
          state.safetyPlanScheduledLists = safetyPlanScheduled;
          state.currentPage = safetyPlanScheduled.pagination.page ?? 1;
        }
      })
      .addCase(fetchSafetyPlanScheduledListsCountAsync.fulfilled, (state, action) => {
        const scheduledListsCountResult: any = action.payload;
        if (state.safetyPlanScheduledLists && action.payload) {
          const countSafetyPlanLists = handleRegisterCountAsyncData(
            scheduledListsCountResult,
            state.safetyPlanScheduledLists,
            'safetyPlanScheduledListtotalPages'
          );
          state.safetyPlanScheduledLists = countSafetyPlanLists;
        }
        state.isLoading = false;
      })

      // HISTORY
      .addCase(fetchSafetyPlanHistoryListAsync.pending, (state) => {
        state.isLoading = true;
        state.safetyPlanHistoryList = {
          items: [],
          pagination: state.safetyPlanHistoryList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchSafetyPlanHistoryListAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const result = action.payload;
          const safetyPlanHistoryList = handleRegisterAsyncData(
            result,
            'safetyPlanHistoryListTotalPages'
          );
          state.isLoading = false;
          state.safetyPlanHistoryList = safetyPlanHistoryList;
        }
      })
      .addCase(fetchSafetyPlanHistoryListAsync.rejected, (state) => {
        state.safetyPlanHistoryList = null;
      })
      .addCase(fetchSafetyPlanHistoryListCountAsync.fulfilled, (state, action) => {
        if (state.safetyPlanHistoryList && action.payload) {
          const countSafetyPlanHistoryList = handleRegisterCountAsyncData(
            action.payload,
            state.safetyPlanHistoryList,
            'safetyPlanHistoryListTotalPages'
          );
          state.safetyPlanHistoryList = countSafetyPlanHistoryList;
          state.isLoading = false;
        }
      });
  },
});

// Memoized Selectors
const safetyPlanState = (state: RootState) => state.safetyPlan;
export const getOhsSafetyPlanLists = createSelector([safetyPlanState], (safetyPlan) => safetyPlan);

export const { setCurrentRegisterPage, setSafetyPlanIsLoading, setViewPreset } =
  safetyPlanSlice.actions;
export const safetyPlanModuleReducer = safetyPlanSlice.reducer;
